import React, { useState } from 'react';
import { ApiClient } from '@docplugin/core';
import { insertContentControl } from '../services/wordHelper';
import { auth, API_URL } from '../services/auth';
import './CreatePlaceholder.css';

const api = new ApiClient({
  baseUrl: API_URL,
  apiKey: 'dp_dev_howden_key123',
  getToken: () => (auth.isAuthenticated() ? auth.getAccessToken() : null),
});

type FieldType = 'Text' | 'Date' | 'Number' | 'Money' | 'Boolean';

export function CreatePlaceholder() {
  const [key, setKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [fieldType, setFieldType] = useState<FieldType>('Text');
  const [required, setRequired] = useState(false);
  const [sampleValue, setSampleValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleInsertAndRegister = async () => {
    if (!key.trim()) {
      setMessage({ type: 'error', text: 'Key is required.' });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      // 1) Insert content control in the document
      await insertContentControl(
        key.trim(),
        displayName.trim() || key.trim(),
        sampleValue.trim() || undefined,
      );

      // 2) Register in the API catalog
      await api.post('/placeholders', {
        key: key.trim(),
        displayName: displayName.trim() || key.trim(),
        type: fieldType,
        required,
        sampleValue: sampleValue.trim() || undefined,
      });

      setMessage({ type: 'success', text: `Placeholder "${key}" inserted and registered.` });
      setKey('');
      setDisplayName('');
      setFieldType('Text');
      setRequired(false);
      setSampleValue('');
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to insert or register placeholder.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="dp-create-placeholder">
      <h3>Create Placeholder</h3>
      <p className="dp-create-placeholder__desc">
        Insert a typed placeholder into the document and register it in the API catalog.
      </p>

      <div className="dp-create-placeholder__form">
        <label className="dp-create-placeholder__label">
          Key
          <input
            type="text"
            className="dp-create-placeholder__input"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="e.g. co.policy.startDate"
          />
        </label>

        <label className="dp-create-placeholder__label">
          Display Name
          <input
            type="text"
            className="dp-create-placeholder__input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Human-readable name (optional)"
          />
        </label>

        <label className="dp-create-placeholder__label">
          Type
          <select
            className="dp-create-placeholder__select"
            value={fieldType}
            onChange={(e) => setFieldType(e.target.value as FieldType)}
          >
            <option value="Text">Text</option>
            <option value="Date">Date</option>
            <option value="Number">Number</option>
            <option value="Money">Money</option>
            <option value="Boolean">Boolean</option>
          </select>
        </label>

        <label className="dp-create-placeholder__checkbox-label">
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
          />
          Required
        </label>

        <label className="dp-create-placeholder__label">
          Sample Value
          <input
            type="text"
            className="dp-create-placeholder__input"
            value={sampleValue}
            onChange={(e) => setSampleValue(e.target.value)}
            placeholder="Example value (optional)"
          />
        </label>
      </div>

      <button
        className="dp-create-placeholder__btn"
        onClick={handleInsertAndRegister}
        disabled={submitting || !key.trim()}
      >
        {submitting ? 'Saving...' : 'Insert & Register'}
      </button>

      {message && (
        <div className={`dp-create-placeholder__message dp-create-placeholder__message--${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
}
